import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseAssessmentAdapter } from './BaseAssessmentAdapter'
import { AdapterOptions, SectionAdapter } from './types'
import { BaseSubsectionAdapter } from './BaseSubsectionAdapter'
import { DischargeFormData } from '../DischargeAssessment/types/formData'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import { mutateFlatAnswers } from '../helpers'
import { AssessmentOverviewAdapter } from '../DischargeAssessment/sections/assessment-overview/adapter'
import { DischargeSummaryAdapter } from '../DischargeAssessment/sections/discharge-summary/adapter'
import { MedicationsAdapter } from '../DischargeAssessment/sections/medications/adapter'
import { PatientOverviewAdapter } from '../DischargeAssessment/sections/patient-overview/adapter'
import { FinalStepsAdapter } from '../DischargeAssessment/sections/final-steps/adapter'
import { MedicalBackgroundAdapter } from '../DischargeAssessment/sections/medical-background/adapter'

/**
 * Array of all discharge section adapter instances
 */
const DISCHARGE_SECTION_ADAPTERS:
  | BaseSubsectionAdapter<DischargeFormData>[]
  | SectionAdapter<DischargeFormData>[] = [
  new AssessmentOverviewAdapter(),
  new DischargeSummaryAdapter(),
  new PatientOverviewAdapter(),
  new MedicationsAdapter(),
  new MedicalBackgroundAdapter(),
  new FinalStepsAdapter()
]

export class DischargeAssessmentAdapter extends BaseAssessmentAdapter<
  DischargeFormData & Record<string, unknown>
> {
  readonly assessmentType = AssessmentType.DISCHARGE

  protected flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): DischargeFormData & Record<string, unknown> {
    // Use composable section adapters for clean, maintainable extraction
    const formData: Partial<DischargeFormData> = {}

    // Extract data from all section adapters dynamically
    for (const adapter of DISCHARGE_SECTION_ADAPTERS) {
      const sectionData = adapter.extractFormData(flatAnswers, options)
      Object.assign(formData, sectionData)
    }

    return formData as DischargeFormData & Record<string, unknown>
  }

  protected formDataToFlatAnswers(
    formData: DischargeFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem> {
    // Use composable section adapters for clean, maintainable transformation
    const allMutations: Array<{ linkId: string; value: unknown }> = []

    // Build mutations from all section adapters dynamically
    for (const adapter of DISCHARGE_SECTION_ADAPTERS) {
      if (adapter instanceof BaseSubsectionAdapter) {
        adapter.setFormData(formData)
        allMutations.push(...adapter.buildFlatAnswers())
      } else if (adapter instanceof SectionAdapter) {
        allMutations.push(...adapter.buildFlatAnswers(formData))
      }
    }

    // Apply all mutations using existing helper
    mutateFlatAnswers(currentFlatAnswers, allMutations)

    return currentFlatAnswers
  }
}
